import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Crown, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";

export default function RolesPage() {
  const qc = useQueryClient();
  const [editRole, setEditRole] = useState(null);
  const [selected, setSelected] = useState([]);
  const [verRent, setVerRent] = useState(false);
  const [verBi, setVerBi] = useState(false);

  const rolesQ = useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/roles")).data });
  const catalogQ = useQuery({ queryKey: ["modules-catalog"], queryFn: async () => (await api.get("/meta/modules")).data });

  const roles = rolesQ.data || [];
  const catalog = catalogQ.data?.catalog || [];
  const groups = catalogQ.data?.groups || [];
  const labelOf = (id) => catalog.find((m) => m.id === id)?.label || id;

  useEffect(() => {
    if (editRole) {
      setSelected(editRole.modules || []);
      setVerRent(!!editRole.ver_rentabilidad);
      setVerBi(!!editRole.ver_bi_agregado);
    }
  }, [editRole]);

  const saveMut = useMutation({
    mutationFn: ({ key, modules, ver_rentabilidad, ver_bi_agregado }) =>
      api.put(`/roles/${key}`, { modules, ver_rentabilidad, ver_bi_agregado }),
    onSuccess: () => { toast.success("Rol actualizado"); qc.invalidateQueries({ queryKey: ["roles"] }); setEditRole(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="space-y-5" data-testid="roles-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" /> Roles y permisos
        </h1>
        <p className="text-sm text-muted-foreground">10 perfiles con sus módulos visibles. Puedes ajustar qué ve cada rol.</p>
      </div>

      {rolesQ.isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 w-full" />)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {roles.map((r) => (
          <Card key={r.key} data-testid={`role-card-${r.key}`} className="flex flex-col">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base leading-tight flex items-center gap-1.5">
                  {r.is_admin ? <Crown className="h-4 w-4 text-primary" /> : r.is_leadership ? <ShieldCheck className="h-4 w-4 text-primary" /> : null}
                  {r.name}
                </CardTitle>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditRole(r)} data-testid={`edit-role-${r.key}`} aria-label="Editar módulos">
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {r.is_admin && <Badge variant="secondary" className="text-[10px]">Admin sistema</Badge>}
                {r.is_leadership && <Badge variant="secondary" className="text-[10px]">Liderazgo</Badge>}
                {r.ver_rentabilidad && !r.is_admin && (
                  <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] hover:bg-amber-100">Rentabilidad Trip</Badge>
                )}
                {r.ver_bi_agregado && !r.is_admin && (
                  <Badge className="bg-rose-100 text-rose-900 border border-rose-300 text-[10px] hover:bg-rose-100">BI € agregado</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              <p className="mb-3 text-xs text-muted-foreground">{r.description}</p>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{r.modules.length} módulos</p>
              <div className="flex flex-wrap gap-1">
                {r.modules.slice(0, 8).map((m) => <Badge key={m} variant="outline" className="text-[10px]">{labelOf(m)}</Badge>)}
                {r.modules.length > 8 && <Badge variant="outline" className="text-[10px]">+{r.modules.length - 8}</Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editRole} onOpenChange={(v) => !v && setEditRole(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="role-edit-dialog">
          <DialogHeader>
            <DialogTitle>Módulos visibles · {editRole?.name}</DialogTitle>
            <DialogDescription>Marca los módulos que este rol podrá ver en la navegación.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {groups.map((g) => {
              const mods = catalog.filter((m) => m.group === g);
              if (mods.length === 0) return null;
              return (
                <div key={g}>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g}</p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {mods.map((m) => (
                      <label key={m.id} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors hover:bg-muted/50">
                        <Checkbox checked={selected.includes(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`module-${m.id}`} />
                        <span className="flex items-center gap-1 truncate">{m.label}{m.built && <Lock className="h-3 w-3 text-success" title="Construido" />}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-2 space-y-2">
            <div className="rounded-lg border border-amber-300/60 bg-amber-50/40 p-3">
              <label className="flex items-center gap-2.5 text-sm">
                <Checkbox checked={verRent} disabled={editRole?.is_admin} onCheckedChange={(v) => setVerRent(!!v)} data-testid="role-ver-rentabilidad" />
                <span className="flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5 text-amber-600" />
                  Ver <strong>Rentabilidad de Viajes</strong> (margen, adelanto, facturas) — panel dentro de cada Trip
                </span>
              </label>
              {editRole?.is_admin && <p className="mt-1 pl-7 text-[11px] text-muted-foreground">El administrador siempre tiene acceso.</p>}
            </div>
            <div className="rounded-lg border border-rose-300/60 bg-rose-50/40 p-3">
              <label className="flex items-center gap-2.5 text-sm">
                <Checkbox checked={verBi} disabled={editRole?.is_admin} onCheckedChange={(v) => setVerBi(!!v)} data-testid="role-ver-bi-agregado" />
                <span className="flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5 text-rose-600" />
                  Ver <strong>Informes BI agregados</strong> (Volumen, Coste, Rentabilidad total en € y tab KimKim)
                </span>
              </label>
              {editRole?.is_admin && <p className="mt-1 pl-7 text-[11px] text-muted-foreground">El administrador siempre tiene acceso.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRole(null)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate({ key: editRole.key, modules: selected, ver_rentabilidad: verRent, ver_bi_agregado: verBi })} disabled={saveMut.isPending} data-testid="role-save-button">
              {saveMut.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
